import React, { useEffect, useState } from 'react';
import TopBanner from '@/components/back-office/TopBanner';
import { useRestaurantsContext } from '@/contexts/RestaurantContext';
import BOLayout from '@/pages/back-office/BOLayout';
import axios from 'axios';
import { toast } from 'react-toastify';
import Pen from '@/assets/media/SVGComponents/Pen';
import Trash from '@/assets/media/SVGComponents/Trash';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from '@/components/Modal';
import Pagination from '../../../components/back-office/Pagination';
import { Pen as LucidePen, Trash as LucideTrash } from 'lucide-react';

const inputStyles = {
	quantity: {
		WebkitAppearance: 'textfield',
		MozAppearance: 'textfield',
		appearance: 'textfield',
	}
};

const ProductsList = () => {
	const { selectedRestaurant } = useRestaurantsContext();
	const [products, setProducts] = useState([]);
	const [filteredProducts, setFilteredProducts] = useState([]);
	const [categories, setCategories] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const role = JSON.parse(localStorage.getItem('role'));
	const [showModal, setShowModal] = useState(false);
	const [productId, setProductId] = useState(null);
	const navigate = useNavigate();
	const location = useLocation();
	const [lastModifiedProduct, setLastModifiedProduct] = useState(null);

	const [productQuantities, setProductQuantities] = useState({});
	const [currentPage, setCurrentPage] = useState(1);
	const productsPerPage = 10;

	useEffect(() => {
		if ((role && !['super-admin', 'admin'].includes(role))) {
			toast.info('Zone interdite', {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});

			navigate('/back-office');

			return;
		}
	}, [role]);

	useEffect(() => {
		if (categories.length > 0) {
			return;
		}

		const fetchCategories = async () => {
			try {
				const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/settings/categories/list`);
				if (response.status === 200) {
					setCategories(response.data);
				}
			} catch (error) {
				console.error('Error fetching categories:', error);
			}
		};

		fetchCategories();
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(
					`${import.meta.env.VITE_API_URL}/api/restaurants/${selectedRestaurant?.id}/products/list`
				);

				if (response.status === 200) {
					setProducts(response.data);

					// Appliquer les filtres existants aux nouvelles données
					let filteredResult = response.data;
					if (selectedCategory) {
						filteredResult = filteredResult.filter(product => product.category === selectedCategory);
						console.log('📊 Nombre de produits après filtre catégorie:', filteredResult.length);
					}
					if (searchTerm) {
						filteredResult = filteredResult.filter(product =>
							product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
							product.category.toLowerCase().includes(searchTerm.toLowerCase())
						);
					}
					setFilteredProducts(filteredResult);

					const initialQuantities = response.data.reduce((acc, product) => {
						acc[product.product_id] = product.quantity;
						return acc;
					}, {});
					setProductQuantities(initialQuantities);

					// Vérifier s'il y a un produit modifié dans l'URL
					const params = new URLSearchParams(location.search);
					const modifiedProductId = params.get('modifiedProduct');
					const categoryFromUrl = params.get('category');

					if (modifiedProductId) {
						setLastModifiedProduct(modifiedProductId);
						// Nettoyer l'URL sans déclencher de re-render
						window.history.replaceState(null, '', location.pathname);
					}

					if (categoryFromUrl) {
						console.log('🔍 Catégorie détectée dans URL:', categoryFromUrl);
						setSelectedCategory(categoryFromUrl);
					}
				} else {
					console.error('❌ Erreur de réponse:', response.status);
					toast.error('Une erreur est survenue');
				}
			} catch (error) {
				console.error('❌ Erreur lors du fetchData:', error);
				toast.error('Une erreur est survenue');
			}
		};

		fetchData();
	}, [selectedRestaurant?.id, selectedCategory, searchTerm]);

	useEffect(() => {
		let result = products;

		if (selectedCategory) {
			result = result.filter(product => product.category === selectedCategory);
		}

		if (searchTerm) {
			result = result.filter(product =>
				product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				product.category.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}
		setFilteredProducts(result);

		// Ne réinitialiser la page que si aucun produit n'est en cours de modification
		if (!lastModifiedProduct) {
			setCurrentPage(1);
		}
	}, [selectedCategory, searchTerm, products]);

	// Effet pour gérer le produit modifié
	useEffect(() => {
		if (lastModifiedProduct && products.length > 0) {
			const targetPage = findProductPage(lastModifiedProduct, filteredProducts);

			setCurrentPage(targetPage);
			setLastModifiedProduct(null); // Réinitialiser après utilisation
		}
	}, [lastModifiedProduct, products, filteredProducts]);

	const handleQuantityChange = (e, productId, isIncrement = null, updateDatabase = true) => {
		// Si c'est un bouton + ou -, on utilise isIncrement
		// Sinon, on utilise la valeur de l'input
		const currentQuantity = productQuantities[productId] || 0;
		let newQuantity;

		if (isIncrement !== null) {
			// Gestion des boutons + et -
			newQuantity = isIncrement ? currentQuantity + 1 : Math.max(0, currentQuantity - 1);
		} else {
			// Gestion de l'input
			newQuantity = parseInt(e.target.value || 0, 10);
		}

		// Mise à jour locale immédiate
		const updatedProducts = products.map(product =>
			product.product_id === productId
				? { ...product, quantity: newQuantity }
				: product
		);
		setProducts(updatedProducts);

		// Mise à jour des produits filtrés
		const updatedFilteredProducts = filteredProducts.map(product =>
			product.product_id === productId
				? { ...product, quantity: newQuantity }
				: product
		);
		setFilteredProducts(updatedFilteredProducts);

		setProductQuantities((prevQuantities) => ({
			...prevQuantities,
			[productId]: newQuantity,
		}));

		// Maintenir la pagination
		setLastModifiedProduct(productId);
		if (!updateDatabase) return;

		// Mise à jour dans la base de données
		updateQuantity(productId, newQuantity);
	};

	// Nouvelle fonction pour trouver la page d'un produit
	const findProductPage = (productId, products) => {
		const productIndex = products.findIndex(product => product.product_id === productId);

		if (productIndex === -1) {
			return 1;
		}

		const page = Math.ceil((productIndex + 1) / productsPerPage);

		return page;
	};

	const updateQuantity = async (productId, quantityToUpdate) => {
		try {
			await axios.patch(
				`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${selectedRestaurant?.id}/products/${productId}`,
				{
					quantity: quantityToUpdate,
				}
			);

			toast.success('Quantité mise à jour avec succès');
		} catch (error) {
			console.error('❌ Erreur lors de la mise à jour de la quantité:', error);
			toast.error('Une erreur est survenue lors de la mise à jour de la quantité');

			// En cas d'erreur, on restaure la quantité précédente
			const previousQuantity = products.find(p => p.product_id === productId)?.quantity || 0;
			setProductQuantities(prev => ({
				...prev,
				[productId]: previousQuantity
			}));
		}
	};

	const handleOpenModal = (e, productId) => {
		setShowModal(true);
		setProductId(productId);
	};

	const handleModalSubmit = async () => {
		try {
			await axios.delete(
				`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${selectedRestaurant?.id}/products/${productId}/delete`
			);

			toast.success('Produit supprimé', {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		} catch (error) {
			console.error('Error deleting product:', error);
			toast.error('Une erreur est survenue', {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}

		setTimeout(() => {
			navigate(0);
		}, 1500);
	};

	const handleToggleActive = async (productId, currentStatus) => {
		try {
			await axios.patch(
				`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${selectedRestaurant?.id}/products/${productId}`,
				{
					is_active: !currentStatus,
				}
			);

			// Mise à jour locale des produits
			const updatedProducts = products.map(product =>
				product.product_id === productId
					? { ...product, is_active: !currentStatus }
					: product
			);
			setProducts(updatedProducts);
			setLastModifiedProduct(productId);

			toast.success(`Produit ${!currentStatus ? 'activé' : 'désactivé'} avec succès`);
		} catch (error) {
			console.error('❌ Erreur lors du toggle:', error);
			toast.error('Une erreur est survenue');
		}
	};

	const handleWeightChange = (e, productId) => {
		const newWeight = parseInt(e.target.value, 10);
		
		if (isNaN(newWeight)) return;

		// Mise à jour locale immédiate
		const updatedProducts = products.map(product =>
			product.product_id === productId
				? { ...product, weight: newWeight }
				: product
		);
		setProducts(updatedProducts);

		// Mise à jour des produits filtrés en préservant le filtre
		const updatedFilteredProducts = filteredProducts.map(product =>
			product.product_id === productId
				? { ...product, weight: newWeight }
				: product
		);
		setFilteredProducts(updatedFilteredProducts);

		// Maintenir la pagination
		setLastModifiedProduct(productId);
	};

	const updateWeight = async (productId) => {
		const product = products.find(p => p.product_id === productId);
		if (!product) return;

		try {
			await axios.patch(
				`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${selectedRestaurant?.id}/products/${productId}`,
				{
					weight: product.weight,
				}
			);

			// Mise à jour locale des produits
			const updatedProducts = products.map(p =>
				p.product_id === productId
					? { ...p, weight: product.weight }
					: p
			);
			setProducts(updatedProducts);
			setLastModifiedProduct(productId);

			toast.success('Poids mis à jour avec succès');
		} catch (error) {
			console.error('❌ Erreur lors de la mise à jour du poids:', error);
			toast.error('Une erreur est survenue lors de la mise à jour du poids');
		}
	};

	const handlePriceChange = (e, productId) => {
		const newPrice = parseFloat(e.target.value);
		// Mise à jour locale immédiate
		const updatedProducts = products.map(product =>
			product.product_id === productId
				? { ...product, price: newPrice }
				: product
		);
		setProducts(updatedProducts);

		// Mise à jour des produits filtrés en préservant le filtre
		const updatedFilteredProducts = filteredProducts.map(product =>
			product.product_id === productId
				? { ...product, price: newPrice }
				: product
		);
		setFilteredProducts(updatedFilteredProducts);

		// Maintenir la pagination
		setLastModifiedProduct(productId);
	};

	const updatePrice = async (productId) => {
		const product = products.find(p => p.product_id === productId);
		if (!product) return;

		try {
			await axios.patch(
				`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${selectedRestaurant?.id}/products/${productId}`,
				{
					price: product.price,
				}
			);

			// Mise à jour locale des produits
			const updatedProducts = products.map(p =>
				p.product_id === productId
					? { ...p, price: product.price }
					: p
			);
			setProducts(updatedProducts);
			setLastModifiedProduct(productId);

			toast.success('Prix mis à jour avec succès');
		} catch (error) {
			console.error('❌ Erreur lors de la mise à jour du prix:', error);
			toast.error('Une erreur est survenue lors de la mise à jour du prix');
		}
	};

	// Pagination
	const indexOfLastProduct = currentPage * productsPerPage;
	const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
	const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

	const onPageChange = (pageNumber) => setCurrentPage(pageNumber);

	return (
		<BOLayout>
			<div className="mb-4">
				<TopBanner
					role={role}
					showButtons={'left'}
					showToggle={false}
					navigationRouteText={'Créer un produit'}
					navigationRoute={`/back-office/restaurants/${selectedRestaurant?.id}/products/creation`}
					hasTopRightButton={['super-admin', 'admin'].includes(role)}
				/>
			</div>

			{showModal && (
				<Modal
					type={'danger'}
					title={'Cette action est irréversible'}
					setShowModal={setShowModal}
					handleSubmit={handleModalSubmit}
				/>
			)}

			<div className="mb-4 flex flex-col md:flex-row gap-4 justify-between">
				<div className="w-full md:w-1/3">
					<input
						type="text"
						placeholder="Rechercher un produit..."
						className="w-full p-2 border border-gray-300 rounded-md"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
				</div>
				<div className="w-full md:w-1/3">
					<select
						className="w-full p-2 border border-gray-300 rounded-md"
						value={selectedCategory}
						onChange={(e) => setSelectedCategory(e.target.value)}
					>
						<option value="">Toutes les catégories</option>
						{categories.map((category) => (
							<option key={category.id} value={category.name}>
								{category.name}
							</option>
						))}
					</select>
				</div>
			</div>

			<table className="table table-striped table-bordered table-fixed w-full text-center">
				<thead className="border-b border-b-black pb-2 mb-4">
					<tr>
						<th>Image</th>
						<th>Nom</th>
						<th>Prix</th>
						<th>Catégorie</th>
						<th>Quantité</th>
						<th>Poids</th>
						<th>Statut</th>
						<th>Actions</th>
					</tr>
				</thead>
				<tbody>
					{currentProducts.map((product) => (
						<tr key={product.product_id} className={!product.is_active ? 'opacity-50' : ''}>
							<td>
								<img
									loading="lazy"
									src={product.picture}
									alt="Placeholder"
									width={80}
									height={80}
									className="mx-auto w-[80px] h-[80px] object-cover"
								/>
							</td>
							<td>{product.name}</td>
							<td>
								<input
									type="number"
									value={product.price || ''}
									min={0}
									step="0.01"
									onChange={(e) => handlePriceChange(e, product.product_id)}
									onBlur={() => updatePrice(product.product_id)}
									className="p-2 border border-black rounded-md w-2/3"
									onWheel={(e) => e.target.blur()}
								/>
								€
							</td>
							<td>{product.category}</td>
							<td>
								<div className="flex items-center justify-center">
									<button
										type="button"
										className="border border-black rounded-l-md px-2 h-[42px] text-lg font-bold bg-gray-100 hover:bg-gray-200"
										onClick={() => handleQuantityChange(null, product.product_id, false)}
									>
										-
									</button>
									<input
										type="number"
										value={productQuantities[product.product_id] || 0}
										min={0}
										onChange={(e) => handleQuantityChange(e, product.product_id, null, false)}
										onBlur={() => updateQuantity(product.product_id, productQuantities[product.product_id])}
										className="p-2 border-y border-black w-16 text-center h-[42px]"
										style={inputStyles.quantity}
										onWheel={(e) => e.target.blur()}
									/>
									<button
										type="button"
										className="border border-black rounded-r-md px-2 h-[42px] text-lg font-bold bg-gray-100 hover:bg-gray-200"
										onClick={() => handleQuantityChange(null, product.product_id, true)}
									>
										+
									</button>
								</div>
							</td>
							<td>
								<input
									type="number"
									value={product.weight}
									min={0}
									onChange={(e) => handleWeightChange(e, product.product_id)}
									onBlur={() => updateWeight(product.product_id)}
									className="p-2 border border-black rounded-md w-2/3"
									onWheel={(e) => e.target.blur()}
								/>
								g
							</td>
							<td>
								<label className="relative inline-flex items-center cursor-pointer">
									<input
										type="checkbox"
										className="sr-only peer"
										checked={product.is_active}
										onChange={() => handleToggleActive(product.product_id, product.is_active)}
									/>
									<div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
								</label>
							</td>
							<td className="flex items-center justify-center gap-2">
								<button
									type="button"
									onClick={() =>
										navigate(`/back-office/restaurants/${selectedRestaurant?.id}/products/${product.product_id}/edition`)
									}
								>
									<Pen color="#A2924A" width={40} height={40} />
								</button>

								<button type="button" className="m-2" onClick={(e) => handleOpenModal(e, product?.product_id)}>
									<Trash color="#A2924A" width={40} height={40} />
								</button>
							</td>
						</tr>
					))}
				</tbody>
			</table>

			<Pagination
				currentPage={currentPage}
				totalItems={filteredProducts.length}
				itemsPerPage={productsPerPage}
				onPageChange={onPageChange}
			/>
		</BOLayout>
	);
};

export default ProductsList;
